import React, { useState, useEffect, useMemo } from "react";
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  CardActions,
  Button,
  IconButton,
  TextField,
  InputAdornment,
  Chip,
  Paper,
  Breadcrumbs,
  Link as MuiLink,
  Divider,
  Stack,
  Drawer,
  Badge,
  Dialog,
  Select,
  MenuItem,
  useTheme,
  useMediaQuery
} from "@mui/material";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { alpha } from "@mui/material/styles";

// Icons
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import VerifiedIcon from "@mui/icons-material/Verified";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import StorefrontIcon from "@mui/icons-material/Storefront";
import SearchIcon from "@mui/icons-material/Search";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import BarChartIcon from "@mui/icons-material/BarChart";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import MapIcon from "@mui/icons-material/Map";
import ShoppingBagIcon from "@mui/icons-material/ShoppingBag";
import TuneIcon from "@mui/icons-material/Tune";
import CloseIcon from "@mui/icons-material/Close";
import SortIcon from "@mui/icons-material/Sort";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";

// Data & Components
import { MARKETPLACE_LOTS } from "../components/Map/marketplaceData";
import { provinceMapping, productionStatsByProvince } from "../components/Map/productionConfig";
import { initializeMap } from "../components/Map/geoUtils";
import ProductImage from "../components/Map/ProductImage";
import MarketplaceOrderModal from "../components/Map/MarketplaceOrderModal";
import ProvinceModal from "../components/Map/ProvinceModal";

const CATEGORIES = [
  "Toutes",
  "Tubercules & Féculents",
  "Fruits",
  "Maraîchage",
  "Cultures de Rente"
];

const QUICK_SUGGESTIONS = [
  { label: "🥔 Manioc", q: "manioc" },
  { label: "🍍 Ananas", q: "ananas" },
  { label: "🌶️ Piment", q: "piment" },
  { label: "🍌 Plantain", q: "plantain" },
  { label: "🍅 Tomate", q: "tomate" },
  { label: "🍆 Aubergine", q: "aubergine" },
  { label: "🍠 Patate douce", q: "patate" },
  { label: "🟢 En stock", status: "in_stock" },
  { label: "⏳ Précommandes", status: "preorder" }
];

const PROVINCE_NAMES = [
  "Estuaire",
  "Haut-Ogooué",
  "Moyen-Ogooué",
  "Ngounié",
  "Nyanga",
  "Ogooué-Ivindo",
  "Ogooué-Lolo",
  "Ogooué-Maritime",
  "Wouleu-Ntem"
];

const MarketplacePage = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // Filtres
  const provinceParam = searchParams.get("province");
  const vueParam = searchParams.get("vue");
  const [selectedProvince, setSelectedProvince] = useState(provinceParam || "Estuaire");
  const [hoveredProvince, setHoveredProvince] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("Toutes");
  const [selectedStatus, setSelectedStatus] = useState("all"); // 'all' | 'in_stock' | 'preorder'
  const [sortBy, setSortBy] = useState("recommended");

  // Navigation par étapes sur mobile ("carte" | "produits")
  const [mobileStep, setMobileStep] = useState(provinceParam || vueParam === "produits" ? "produits" : "carte");
  // Tiroir de filtres mobile
  const [filterDrawerOpen, setFilterDrawerOpen] = useState(false);

  // Modales
  const [orderModalOpen, setOrderModalOpen] = useState(false);
  const [activeLot, setActiveLot] = useState(null);
  const [chartModalProvince, setChartModalProvince] = useState(null);
  const [unavailableModalOpen, setUnavailableModalOpen] = useState(false);
  const [unavailableProvinceName, setUnavailableProvinceName] = useState("");

  // Nombre de récoltes dans l'Estuaire
  const estuaireLotsCount = useMemo(() => {
    return MARKETPLACE_LOTS.filter((l) => l.province === "Estuaire").length;
  }, []);

  // Synchronisation bidirectionnelle avec l'historique du navigateur
  useEffect(() => {
    if (provinceParam === "Estuaire") {
      setSelectedProvince("Estuaire");
      setMobileStep("produits");
    } else if (provinceParam && PROVINCE_NAMES.includes(provinceParam)) {
      setUnavailableProvinceName(provinceParam);
      setUnavailableModalOpen(true);
      setSelectedProvince("Estuaire");
      setMobileStep("produits");
    } else if (vueParam === "produits") {
      setSelectedProvince("Estuaire");
      setMobileStep("produits");
    } else {
      setSelectedProvince("Estuaire");
      setMobileStep("carte");
    }
  }, [provinceParam, vueParam]);

  // Nombre de filtres actifs
  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (selectedCategory !== "Toutes") count++;
    if (selectedStatus !== "all") count++;
    if (sortBy !== "recommended") count++;
    if (searchQuery.trim() !== "") count++;
    return count;
  }, [selectedCategory, selectedStatus, sortBy, searchQuery]);

  // Sélection d'une province : si Estuaire -> récoltes relatives ; si autre province -> modal professionnelle explicative
  const handleSelectProvince = (provinceName) => {
    if (!provinceName || provinceName === "Estuaire") {
      setSelectedProvince("Estuaire");
      const isComingFromCarte = mobileStep === "carte";

      if (isComingFromCarte && isMobile) {
        setTimeout(() => {
          setSearchParams({ province: "Estuaire" }, { replace: false });
          setMobileStep("produits");
          window.scrollTo({ top: 0, behavior: "smooth" });
        }, 450);
      } else {
        setSearchParams({ province: "Estuaire" }, { replace: !isComingFromCarte });
        setMobileStep("produits");
        window.scrollTo({ top: 0, behavior: "smooth" });
      }
      return;
    }

    // Province grisée non disponible en phase pilote
    setUnavailableProvinceName(provinceName);
    setUnavailableModalOpen(true);
  };

  // Bascule d'étape mobile avec gestion d'historique
  const handleSwitchStep = (step) => {
    if (step === "carte") {
      setMobileStep("carte");
      if (provinceParam || vueParam) {
        if (window.history.length > 1) {
          navigate(-1);
        } else {
          navigate("/centrale-achat", { replace: true });
        }
      }
    } else {
      setSelectedProvince("Estuaire");
      setSearchParams({ province: "Estuaire" }, { replace: false });
      setMobileStep("produits");
    }
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  // Code de la province active pour D3 (fixé sur GA1 pour l'Estuaire)
  const activeProvinceCode = useMemo(() => {
    if (!selectedProvince) return "GA1";
    const entry = Object.entries(provinceMapping).find(([, name]) => name === selectedProvince);
    return entry ? entry[0] : "GA1";
  }, [selectedProvince]);

  // Initialisation de la carte SVG D3 dans #marketplace-map
  useEffect(() => {
    // Si mobile et sur l'étape produits, pas besoin d'initialiser D3
    if (isMobile && mobileStep !== "carte") return;

    const timer = setTimeout(() => {
      initializeMap(
        isMobile,
        (provinceId) => {
          const provinceName = provinceMapping[provinceId];
          if (provinceId === "GA1" || provinceName === "Estuaire") {
            handleSelectProvince("Estuaire");
          } else {
            setUnavailableProvinceName(provinceName || "cette province");
            setUnavailableModalOpen(true);
          }
        },
        (provinceId, isOp) => {
          if (provinceId) {
            setHoveredProvince({
              name: provinceMapping[provinceId],
              isOperational: isOp ?? (provinceId === "GA1")
            });
          } else {
            setHoveredProvince(null);
          }
        },
        "#marketplace-map",
        "GA1"
      );
    }, 100);

    return () => clearTimeout(timer);
  }, [isMobile, mobileStep, activeProvinceCode]);

  // Fonction de normalisation insensible aux accents
  const normalizeText = (text) => {
    return (text || "")
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .toLowerCase()
      .trim();
  };

  // Filtrage et recherche optimisée des lots
  const filteredLots = useMemo(() => {
    const tokens = normalizeText(searchQuery)
      .split(/\s+/)
      .filter(Boolean);

    let result = MARKETPLACE_LOTS.filter((lot) => {
      // Pour la phase actuelle, opérations exclusivement dans l'Estuaire
      if (selectedProvince && lot.province !== selectedProvince) {
        return false;
      }
      // Filtre catégorie
      if (selectedCategory !== "Toutes" && lot.category !== selectedCategory) {
        return false;
      }
      // Filtre statut
      if (selectedStatus !== "all" && lot.status !== selectedStatus) {
        return false;
      }
      // Recherche textuelle optimisée et multi-mots
      if (tokens.length > 0) {
        const prod = normalizeText(lot.product);
        const variety = normalizeText(lot.variety);
        const cat = normalizeText(lot.category);
        const desc = normalizeText(lot.description);
        const certs = (lot.certifications || []).map(normalizeText).join(" ");

        const matchesAll = tokens.every(
          (t) =>
            prod.includes(t) ||
            variety.includes(t) ||
            cat.includes(t) ||
            desc.includes(t) ||
            certs.includes(t)
        );
        if (!matchesAll) return false;
      }
      return true;
    });

    // Tri dynamique
    if (sortBy === "price_asc") {
      result.sort((a, b) => a.pricePerKg - b.pricePerKg);
    } else if (sortBy === "price_desc") {
      result.sort((a, b) => b.pricePerKg - a.pricePerKg);
    } else if (sortBy === "tonnes_desc") {
      result.sort((a, b) => b.availableTonnes - a.availableTonnes);
    } else if (sortBy === "stock_first") {
      result.sort((a, b) => {
        if (a.status === "in_stock" && b.status !== "in_stock") return -1;
        if (b.status === "in_stock" && a.status !== "in_stock") return 1;
        return 0;
      });
    }

    return result;
  }, [selectedProvince, selectedCategory, selectedStatus, searchQuery, sortBy]);

  // Nombre de récoltes par catégorie dans l'Estuaire
  const categoryCounts = useMemo(() => {
    const counts = { Toutes: estuaireLotsCount };
    CATEGORIES.forEach((cat) => {
      if (cat !== "Toutes") {
        counts[cat] = MARKETPLACE_LOTS.filter(
          (l) => l.province === "Estuaire" && l.category === cat
        ).length;
      }
    });
    return counts;
  }, [estuaireLotsCount]);

  // Ouvrir la commande pour un lot
  const handleOpenOrder = (lot) => {
    setActiveLot(lot);
    setOrderModalOpen(true);
  };

  // Ouvrir les statistiques de la province
  const handleOpenProvinceStats = (provinceName) => {
    const isEstuaire = provinceName === "Estuaire" || provinceName === "GA1";
    setChartModalProvince({
      name: provinceName,
      stats: isEstuaire ? (productionStatsByProvince[provinceName] || []) : [],
      dataUnavailable: !isEstuaire
    });
  };

  // Réinitialiser tous les filtres
  const handleResetFilters = () => {
    setSelectedProvince("Estuaire");
    setSelectedCategory("Toutes");
    setSelectedStatus("all");
    setSortBy("recommended");
    setSearchQuery("");
  };

  return (
    <>
      <Helmet>
        <title>Centrale d'Achat Agricole & Marketplace | Agri Vision Gabon</title>
        <meta
          name="description"
          content="Centrale d'Achat Agricole du Gabon : achetez et précommandez directement les récoltes certifiées de l'Estuaire avec livraison garantie dans le Grand Libreville (Akanda, Owendo, Libreville)."
        />
        <meta property="og:title" content="Centrale d'Achat Agricole & Marketplace | Agri Vision Gabon" />
        <meta
          property="og:description"
          content="Approvisionnement direct bord champ dans l'Estuaire, traçabilité 100% AVG Farm et livraison sécurisée sous 24h."
        />
      </Helmet>

      {/* Hero Header de la Centrale d'Achat */}
      <Box
        sx={{
          bgcolor: "#01321d",
          backgroundImage:
            "radial-gradient(circle at 15% 40%, rgba(16, 185, 129, 0.2) 0%, transparent 50%), radial-gradient(circle at 85% 20%, rgba(245, 158, 11, 0.18) 0%, transparent 45%)",
          color: "#ffffff",
          pt: { xs: 1.2, sm: 3.5, md: 5 },
          pb: { xs: 1.2, sm: 3.5, md: 5 },
          borderBottom: "1px solid rgba(255, 255, 255, 0.08)"
        }}
      >
        <Container maxWidth="lg">
          {/* Fil d'Ariane (Tablette & Desktop) */}
          <Breadcrumbs
            separator={<NavigateNextIcon fontSize="small" sx={{ color: "rgba(255,255,255,0.5)" }} />}
            aria-label="breadcrumb"
            sx={{ mb: 2, display: { xs: "none", sm: "flex" } }}
          >
            <MuiLink
              component={Link}
              to="/"
              sx={{
                color: "rgba(255,255,255,0.7)",
                textDecoration: "none",
                fontSize: "0.85rem",
                "&:hover": { color: "#ffffff" }
              }}
            >
              Accueil
            </MuiLink>
            <Typography sx={{ color: "#ffffff", fontSize: "0.85rem", fontWeight: 700 }}>
              Centrale d'Achat & Marketplace Agricole
            </Typography>
          </Breadcrumbs>

          {/* Titre & Badges */}
          <Box sx={{ maxWidth: "920px" }}>
            <Box sx={{ display: { xs: "none", sm: "flex" }, alignItems: "center", gap: 1, mb: 1, flexWrap: "wrap" }}>
              <Chip
                icon={<VerifiedIcon sx={{ fontSize: "14px !important", color: "#10b981 !important" }} />}
                label="Plateforme Officielle AVG Market"
                size="small"
                sx={{
                  bgcolor: "rgba(16, 185, 129, 0.18)",
                  color: "#34d399",
                  fontWeight: 700,
                  fontSize: "0.75rem",
                  border: "1px solid rgba(16, 185, 129, 0.35)"
                }}
              />
              <Chip
                label="Phase Pilote : Estuaire (8 Récoltes)"
                size="small"
                sx={{
                  bgcolor: "rgba(255, 255, 255, 0.15)",
                  color: "#ffffff",
                  fontWeight: 700,
                  fontSize: "0.75rem",
                  border: "1px solid rgba(255, 255, 255, 0.25)"
                }}
              />
              <Chip
                label="Traçabilité 100% AVG"
                size="small"
                sx={{
                  bgcolor: "rgba(245, 158, 11, 0.18)",
                  color: "#fbbf24",
                  fontWeight: 700,
                  fontSize: "0.75rem",
                  border: "1px solid rgba(245, 158, 11, 0.3)"
                }}
              />
            </Box>

            <Typography
              variant="h3"
              component="h1"
              sx={{
                fontWeight: 900,
                fontSize: { xs: "1.28rem", sm: "2.4rem", md: "3.2rem" },
                lineHeight: 1.15,
                mb: { xs: 0.2, sm: 1 },
                letterSpacing: "-0.5px"
              }}
            >
              Centrale d'Achat Agricole
            </Typography>

            <Typography
              variant="body1"
              sx={{
                color: "rgba(255, 255, 255, 0.88)",
                fontSize: { xs: "0.78rem", sm: "1rem", md: "1.08rem" },
                lineHeight: { xs: 1.35, sm: 1.5 },
                mb: { xs: 0, sm: 2.5 }
              }}
            >
              Phase Pilote : Grand Libreville (Akanda, Owendo, Libreville) • Extension progressive dans les autres provinces en Phase 2 • Traçabilité AVG Farm
            </Typography>

            {/* Piliers de confiance (Tablette & Desktop) */}
            <Box sx={{ display: { xs: "none", sm: "flex" }, flexWrap: "wrap", gap: 3, mt: 1.5 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.8 }}>
                <VerifiedIcon sx={{ color: "#34d399", fontSize: 18 }} />
                <Typography variant="body2" sx={{ color: "#ffffff", fontWeight: 600, fontSize: "0.85rem" }}>
                  Direct Coopératives
                </Typography>
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.8 }}>
                <LocalShippingIcon sx={{ color: "#fbbf24", fontSize: 18 }} />
                <Typography variant="body2" sx={{ color: "#ffffff", fontWeight: 600, fontSize: "0.85rem" }}>
                  Livraison 24h-48h
                </Typography>
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.8 }}>
                <WhatsAppIcon sx={{ color: "#25D366", fontSize: 18 }} />
                <Typography variant="body2" sx={{ color: "#ffffff", fontWeight: 600, fontSize: "0.85rem" }}>
                  WhatsApp (+241 04 12 64 88)
                </Typography>
              </Box>
            </Box>
          </Box>
        </Container>
      </Box>

      {/* Barre d'étapes mobile interactive (Navigation sans défilement) */}
      <Box
        sx={{
          display: { xs: "block", md: "none" },
          bgcolor: "#ffffff",
          px: 2,
          py: 1.2,
          position: "sticky",
          top: 0,
          zIndex: 100,
          borderBottom: "1px solid rgba(1, 69, 39, 0.12)",
          boxShadow: "0 2px 10px rgba(0,0,0,0.06)"
        }}
      >
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            bgcolor: alpha("#014527", 0.08),
            borderRadius: "32px",
            p: 0.5,
            gap: 0.5
          }}
        >
          <Button
            onClick={() => handleSwitchStep("carte")}
            startIcon={<MapIcon sx={{ fontSize: "17px !important" }} />}
            sx={{
              borderRadius: "28px",
              py: 0.85,
              fontWeight: 800,
              fontSize: "0.8rem",
              textTransform: "none",
              bgcolor: mobileStep === "carte" ? "#014527" : "transparent",
              color: mobileStep === "carte" ? "#ffffff" : "#01321d",
              boxShadow: mobileStep === "carte" ? "0 2px 8px rgba(1, 69, 39, 0.3)" : "none",
              "&:hover": {
                bgcolor: mobileStep === "carte" ? "#01321d" : alpha("#014527", 0.12)
              }
            }}
          >
            Carte {selectedProvince ? `(${selectedProvince})` : "du Gabon"}
          </Button>

          <Button
            onClick={() => handleSwitchStep("produits")}
            startIcon={<ShoppingBagIcon sx={{ fontSize: "17px !important" }} />}
            sx={{
              borderRadius: "28px",
              py: 0.85,
              fontWeight: 800,
              fontSize: "0.8rem",
              textTransform: "none",
              bgcolor: mobileStep === "produits" ? "#014527" : "transparent",
              color: mobileStep === "produits" ? "#ffffff" : "#01321d",
              boxShadow: mobileStep === "produits" ? "0 2px 8px rgba(1, 69, 39, 0.3)" : "none",
              "&:hover": {
                bgcolor: mobileStep === "produits" ? "#01321d" : alpha("#014527", 0.12)
              }
            }}
          >
            Récoltes ({filteredLots.length})
          </Button>
        </Box>
      </Box>

      {/* Section Carte Interactive & Snapshot de Province */}
      <Box sx={{ bgcolor: "#f3f5f3", py: { xs: 2.5, md: 5 }, display: { xs: mobileStep === "carte" ? "block" : "none", md: "block" } }}>
        <Container maxWidth="lg">
          <Paper
            elevation={2}
            sx={{
              borderRadius: "16px",
              p: { xs: 2, sm: 3, md: 4 },
              bgcolor: "#ffffff",
              border: "1px solid rgba(1, 69, 39, 0.1)"
            }}
          >
            <Grid container spacing={3} alignItems="center">
              {/* Carte SVG Interactive du Gabon */}
              <Grid item xs={12} md={7}>
                <Box sx={{ mb: 1.5, display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 1 }}>
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 800, color: "#01321d", fontSize: { xs: "1.05rem", sm: "1.25rem" } }}>
                      Carte Interactive • Zoom sur la Province de l'Estuaire
                    </Typography>
                    <Typography variant="caption" sx={{ color: "text.secondary" }}>
                      Phase pilote exclusive • Approvisionnement du Grand Libreville (Akanda, Owendo, Libreville)
                    </Typography>
                  </Box>
                  <Chip
                    icon={<VerifiedIcon sx={{ fontSize: "14px !important", color: "#ffffff !important" }} />}
                    label="Phase 1 Active : Estuaire"
                    size="small"
                    sx={{ bgcolor: "#15803d", color: "#ffffff", fontWeight: 800, fontSize: "0.75rem" }}
                  />
                </Box>

                {/* Conteneur de la carte D3 (zéro coupure avec fitExtent) */}
                <Box
                  sx={{
                    width: "100%",
                    height: { xs: "280px", sm: "400px", md: "460px" },
                    bgcolor: alpha("#014527", 0.03),
                    borderRadius: "12px",
                    border: "1px solid rgba(1, 69, 39, 0.15)",
                    overflow: "hidden",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    position: "relative"
                  }}
                >
                  <div
                    id="marketplace-map"
                    style={{
                      width: "100%",
                      height: "100%",
                      display: "block"
                    }}
                  />

                  {/* Info survol en bas de carte */}
                  {hoveredProvince && (
                    <Box
                      sx={{
                        position: "absolute",
                        bottom: 12,
                        left: "50%",
                        transform: "translateX(-50%)",
                        bgcolor: hoveredProvince.isOperational ? "rgba(1, 50, 29, 0.94)" : "rgba(30, 41, 59, 0.94)",
                        color: "#ffffff",
                        px: 2,
                        py: 0.6,
                        borderRadius: "20px",
                        fontSize: "0.78rem",
                        fontWeight: 700,
                        pointerEvents: "none",
                        boxShadow: "0 4px 12px rgba(0,0,0,0.25)",
                        border: hoveredProvince.isOperational
                          ? "1px solid rgba(34, 197, 94, 0.4)"
                          : "1px solid rgba(148, 163, 184, 0.3)"
                      }}
                    >
                      {hoveredProvince.isOperational
                        ? "📍 Province de l'Estuaire • Phase 1 Active (8 récoltes disponibles)"
                        : `📍 Province de ${hoveredProvince.name} • Bientôt disponible (Phase 2)`}
                    </Box>
                  )}
                </Box>

                {/* Bouton d'accès direct aux récoltes de l'Estuaire sur Mobile */}
                <Box sx={{ display: { xs: "block", md: "none" }, mt: 1.5 }}>
                  <Button
                    variant="contained"
                    fullWidth
                    endIcon={<NavigateNextIcon />}
                    onClick={() => {
                      setSelectedProvince("Estuaire");
                      handleSwitchStep("produits");
                    }}
                    sx={{
                      bgcolor: "#014527",
                      color: "#ffffff",
                      fontWeight: 800,
                      borderRadius: "14px",
                      py: 1.25,
                      fontSize: "0.88rem",
                      boxShadow: "0 4px 14px rgba(1, 69, 39, 0.28)",
                      "&:hover": { bgcolor: "#01321d" }
                    }}
                  >
                    Voir les {estuaireLotsCount} récoltes dans l'Estuaire →
                  </Button>
                </Box>
              </Grid>

              {/* Panneau latéral : Fiche de la province de l'Estuaire (Visible sur Desktop uniquement) */}
              <Grid item xs={12} md={5} sx={{ display: { xs: "none", md: "block" } }}>
                <Box
                  sx={{
                    p: { xs: 2, sm: 3 },
                    bgcolor: alpha("#014527", 0.04),
                    borderRadius: "14px",
                    border: "2px solid #014527"
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.8 }}>
                      <Chip
                        label="GA1"
                        size="small"
                        sx={{ bgcolor: "#014527", color: "#ffffff", fontWeight: 800 }}
                      />
                      <Chip
                        label="Phase Pilote Active"
                        size="small"
                        sx={{ bgcolor: "#15803d", color: "#ffffff", fontWeight: 800, fontSize: "0.72rem" }}
                      />
                    </Box>
                    <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 600 }}>
                      Chef-lieu : Libreville
                    </Typography>
                  </Box>

                  <Typography variant="h5" sx={{ fontWeight: 900, color: "#01321d", mb: 0.5 }}>
                    Province de l'Estuaire
                  </Typography>

                  <Typography variant="subtitle2" sx={{ color: "#059669", fontWeight: 700, mb: 1.5 }}>
                    🌾 Approvisionnement exclusif Grand Libreville
                  </Typography>

                  <Typography variant="body2" sx={{ color: "text.secondary", lineHeight: 1.5, mb: 2.5, fontSize: "0.85rem" }}>
                    Périmètre d'approvisionnement et de livraison garanti dans le Grand Libreville (Akanda, Owendo, Libreville). Récoltes certifiées bord champ livrées sous 24h.
                  </Typography>

                  <Divider sx={{ mb: 2 }} />

                  {/* Indicateurs clés */}
                  <Grid container spacing={1.5} sx={{ mb: 2.5 }}>
                    <Grid item xs={4}>
                      <Paper elevation={0} sx={{ p: 1, textAlign: "center", bgcolor: "#ffffff", borderRadius: "8px" }}>
                        <Typography variant="h6" sx={{ fontWeight: 800, color: "#014527", fontSize: "1rem" }}>
                          145
                        </Typography>
                        <Typography variant="caption" sx={{ fontSize: "0.68rem", color: "text.secondary", display: "block" }}>
                          Producteurs tracés
                        </Typography>
                      </Paper>
                    </Grid>
                    <Grid item xs={4}>
                      <Paper elevation={0} sx={{ p: 1, textAlign: "center", bgcolor: "#ffffff", borderRadius: "8px" }}>
                        <Typography variant="h6" sx={{ fontWeight: 800, color: "#d97706", fontSize: "1rem" }}>
                          420 ha
                        </Typography>
                        <Typography variant="caption" sx={{ fontSize: "0.68rem", color: "text.secondary", display: "block" }}>
                          Superficie suivie
                        </Typography>
                      </Paper>
                    </Grid>
                    <Grid item xs={4}>
                      <Paper elevation={0} sx={{ p: 1, textAlign: "center", bgcolor: "#ffffff", borderRadius: "8px" }}>
                        <Typography variant="h6" sx={{ fontWeight: 800, color: "#2563eb", fontSize: "0.85rem", mt: 0.3 }}>
                          24h
                        </Typography>
                        <Typography variant="caption" sx={{ fontSize: "0.68rem", color: "text.secondary", display: "block" }}>
                          Grand Libreville
                        </Typography>
                      </Paper>
                    </Grid>
                  </Grid>

                  {/* Actions sur la province */}
                  <Stack spacing={1.2}>
                    <Button
                      variant="contained"
                      endIcon={<NavigateNextIcon />}
                      onClick={() => {
                        const catalogueElem = document.getElementById("catalogue-recoltes");
                        if (catalogueElem) {
                          catalogueElem.scrollIntoView({ behavior: "smooth" });
                        }
                      }}
                      sx={{
                        bgcolor: "#014527",
                        color: "#ffffff",
                        fontWeight: 800,
                        textTransform: "none",
                        borderRadius: "10px",
                        py: 1.1,
                        "&:hover": { bgcolor: "#01321d" }
                      }}
                      fullWidth
                    >
                      Voir les {estuaireLotsCount} récoltes dans l'Estuaire →
                    </Button>

                    <Button
                      variant="outlined"
                      startIcon={<BarChartIcon />}
                      onClick={() => handleOpenProvinceStats("Estuaire")}
                      sx={{
                        borderColor: alpha("#014527", 0.4),
                        color: "#014527",
                        fontWeight: 700,
                        textTransform: "none",
                        borderRadius: "10px"
                      }}
                      fullWidth
                    >
                      Graphiques de Production Estuaire (Recharts)
                    </Button>

                    {/* Note informative Phase Pilote */}
                    <Paper
                      elevation={0}
                      sx={{
                        p: 1.4,
                        bgcolor: "#f8fafc",
                        borderRadius: "10px",
                        border: "1px dashed #cbd5e1",
                        display: "flex",
                        alignItems: "flex-start",
                        gap: 1
                      }}
                    >
                      <InfoOutlinedIcon sx={{ fontSize: 18, color: "#64748b", mt: 0.2, flexShrink: 0 }} />
                      <Typography variant="caption" sx={{ color: "#475569", lineHeight: 1.45 }}>
                        <strong>Note d'exploitation :</strong> Agri Vision Gabon déploie sa centrale d'achat en priorité pour approvisionner le <strong>Grand Libreville (Akanda, Owendo, Libreville)</strong>. Les 8 autres provinces du Gabon sont grisées et s'ouvriront progressivement lors de la Phase 2.
                      </Typography>
                    </Paper>
                  </Stack>
                </Box>
              </Grid>
            </Grid>
          </Paper>
        </Container>
      </Box>

      {/* Catalogue des Lots Agricoles */}
      <Box id="catalogue-recoltes" sx={{ bgcolor: "#ffffff", py: { xs: 2.5, md: 6 }, display: { xs: mobileStep === "produits" ? "block" : "none", md: "block" } }}>
        <Container maxWidth="lg">
          {/* Barre de Recherche & Filtres */}
          <Box sx={{ mb: 3 }}>
            {/* Recherche + Bouton Filtres sur Mobile */}
            <Box sx={{ display: { xs: "flex", md: "none" }, gap: 1, mb: 1.5 }}>
              <TextField
                placeholder="Rechercher manioc, ananas, piment, tomate..."
                size="small"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                fullWidth
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "24px",
                    bgcolor: "#f9faf9"
                  }
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ color: "text.secondary" }} />
                    </InputAdornment>
                  ),
                  endAdornment: searchQuery ? (
                    <InputAdornment position="end">
                      <IconButton size="small" onClick={() => setSearchQuery("")}>
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    </InputAdornment>
                  ) : null
                }}
              />

              <Badge
                badgeContent={activeFilterCount}
                color="warning"
                sx={{ "& .MuiBadge-badge": { fontWeight: 900 } }}
              >
                <Button
                  variant="contained"
                  onClick={() => setFilterDrawerOpen(true)}
                  startIcon={<TuneIcon sx={{ fontSize: "18px !important" }} />}
                  sx={{
                    bgcolor: activeFilterCount > 0 ? "#014527" : "#f1f5f2",
                    color: activeFilterCount > 0 ? "#ffffff" : "#01321d",
                    fontWeight: 800,
                    borderRadius: "24px",
                    px: 2,
                    whiteSpace: "nowrap",
                    minWidth: "auto",
                    border: "1px solid rgba(1, 69, 39, 0.2)",
                    textTransform: "none",
                    boxShadow: "none",
                    "&:hover": {
                      bgcolor: activeFilterCount > 0 ? "#01321d" : "#e5ebe6"
                    }
                  }}
                >
                  Filtres
                </Button>
              </Badge>
            </Box>

            {/* Titre & Recherche sur Desktop */}
            <Box sx={{ display: { xs: "none", md: "flex" }, justifyContent: "space-between", alignItems: "flex-end", mb: 2, flexWrap: "wrap", gap: 2 }}>
              <Box>
                <Typography variant="h4" component="h2" sx={{ fontWeight: 900, color: "#01321d", fontSize: { xs: "1.6rem", md: "2.1rem" } }}>
                  Catalogue des Récoltes • Grand Libreville
                </Typography>
                <Typography variant="body2" sx={{ color: "text.secondary", mt: 0.5 }}>
                  {filteredLots.length} {filteredLots.length > 1 ? "lots certifiés disponibles" : "lot certifié disponible"} avec livraison garantie dans le Grand Libreville (Akanda, Owendo, Libreville)
                </Typography>
              </Box>

              {/* Champ de recherche sur Desktop */}
              <TextField
                placeholder="Rechercher manioc, ananas, piment, plantain, tomate..."
                size="small"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                sx={{
                  minWidth: "340px",
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "24px",
                    bgcolor: "#f9faf9"
                  }
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ color: "text.secondary" }} />
                    </InputAdornment>
                  ),
                  endAdornment: searchQuery ? (
                    <InputAdornment position="end">
                      <IconButton size="small" onClick={() => setSearchQuery("")}>
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    </InputAdornment>
                  ) : null
                }}
              />
            </Box>

            {/* Suggestions rapides en 1 clic */}
            <Box
              sx={{
                display: "flex",
                gap: 0.8,
                overflowX: "auto",
                pb: 0.8,
                mb: 1.5,
                "&::-webkit-scrollbar": { display: "none" },
                scrollbarWidth: "none"
              }}
            >
              <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 700, alignSelf: "center", mr: 0.5, flexShrink: 0, display: { xs: "none", sm: "block" } }}>
                Suggestions rapides :
              </Typography>
              {QUICK_SUGGESTIONS.map((sug) => {
                const isActive = sug.status
                  ? selectedStatus === sug.status
                  : sug.q && searchQuery.toLowerCase().includes(sug.q);
                return (
                  <Chip
                    key={sug.label}
                    label={sug.label}
                    size="small"
                    onClick={() => {
                      if (sug.status) {
                        setSelectedStatus(selectedStatus === sug.status ? "all" : sug.status);
                      } else if (sug.q) {
                        setSearchQuery(searchQuery.toLowerCase() === sug.q ? "" : sug.q);
                      }
                    }}
                    sx={{
                      flexShrink: 0,
                      bgcolor: isActive ? "#014527" : "#ffffff",
                      color: isActive ? "#ffffff" : "#01321d",
                      fontWeight: 700,
                      fontSize: "0.75rem",
                      border: isActive ? "1px solid #014527" : "1px solid rgba(1, 69, 39, 0.2)",
                      cursor: "pointer",
                      "&:hover": {
                        bgcolor: isActive ? "#01321d" : alpha("#014527", 0.08)
                      }
                    }}
                  />
                );
              })}
            </Box>

            {/* Filtres par Catégorie avec compteurs de lots */}
            <Box
              sx={{
                display: "flex",
                gap: 1,
                overflowX: "auto",
                pb: 0.8,
                mb: 1.8,
                "&::-webkit-scrollbar": { display: "none" },
                scrollbarWidth: "none"
              }}
            >
              {CATEGORIES.map((cat) => {
                const count = categoryCounts[cat] ?? 0;
                const isSelected = selectedCategory === cat;
                return (
                  <Chip
                    key={cat}
                    label={`${cat} (${count})`}
                    clickable
                    onClick={() => setSelectedCategory(cat)}
                    sx={{
                      flexShrink: 0,
                      bgcolor: isSelected ? "#014527" : "#f1f5f2",
                      color: isSelected ? "#ffffff" : "#01321d",
                      fontWeight: 700,
                      fontSize: "0.8rem",
                      border: isSelected ? "1px solid #014527" : "1px solid rgba(1, 69, 39, 0.15)",
                      "&:hover": {
                        bgcolor: isSelected ? "#01321d" : "#e5ebe6"
                      }
                    }}
                  />
                );
              })}
            </Box>

            {/* Barre d'outils avancée : Périmètre Grand Libreville + Disponibilité + Tri */}
            <Box
              sx={{
                display: "flex",
                gap: 1.5,
                alignItems: "center",
                flexWrap: "wrap",
                p: 1.5,
                bgcolor: "#f9faf9",
                borderRadius: "14px",
                border: "1px solid rgba(1, 69, 39, 0.1)"
              }}
            >
              {/* Périmètre de livraison garanti */}
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.8, flexWrap: "wrap" }}>
                <LocalShippingIcon sx={{ fontSize: 18, color: "#059669" }} />
                <Typography variant="caption" sx={{ fontWeight: 800, color: "#01321d" }}>
                  Livraison :
                </Typography>
                <Chip
                  label="Grand Libreville (Akanda, Owendo, Libreville)"
                  size="small"
                  sx={{
                    bgcolor: alpha("#014527", 0.08),
                    color: "#014527",
                    fontWeight: 800,
                    fontSize: "0.74rem",
                    border: "1px solid rgba(1, 69, 39, 0.2)"
                  }}
                />
              </Box>

              <Divider orientation="vertical" flexItem sx={{ display: { xs: "none", sm: "block" } }} />

              {/* Disponibilité */}
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.8, flexWrap: "wrap" }}>
                <Typography variant="caption" sx={{ fontWeight: 800, color: "#01321d" }}>
                  Disponibilité :
                </Typography>
                {[
                  { id: "all", label: "Tous" },
                  { id: "in_stock", label: "🟢 En stock" },
                  { id: "preorder", label: "⏳ Précommande" }
                ].map((st) => {
                  const isSelected = selectedStatus === st.id;
                  return (
                    <Chip
                      key={st.id}
                      label={st.label}
                      size="small"
                      onClick={() => setSelectedStatus(st.id)}
                      sx={{
                        bgcolor: isSelected ? (st.id === "in_stock" ? "#059669" : st.id === "preorder" ? "#d97706" : "#01321d") : "#ffffff",
                        color: isSelected ? "#ffffff" : "text.primary",
                        fontWeight: isSelected ? 800 : 600,
                        fontSize: "0.74rem",
                        border: isSelected ? "none" : "1px solid rgba(0,0,0,0.12)",
                        cursor: "pointer"
                      }}
                    />
                  );
                })}
              </Box>

              <Divider orientation="vertical" flexItem sx={{ display: { xs: "none", md: "block" } }} />

              {/* Tri */}
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.8, ml: { xs: 0, md: "auto" } }}>
                <SortIcon sx={{ fontSize: 18, color: "text.secondary" }} />
                <Typography variant="caption" sx={{ fontWeight: 800, color: "#01321d" }}>
                  Trier :
                </Typography>
                <Select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  size="small"
                  sx={{
                    fontSize: "0.78rem",
                    fontWeight: 700,
                    borderRadius: "10px",
                    bgcolor: "#ffffff",
                    height: 32,
                    "& .MuiSelect-select": { py: 0.5, px: 1.2 }
                  }}
                >
                  <MenuItem value="recommended" sx={{ fontSize: "0.8rem" }}>Recommandé / Disponibilité</MenuItem>
                  <MenuItem value="price_asc" sx={{ fontSize: "0.8rem" }}>Prix / kg croissant</MenuItem>
                  <MenuItem value="price_desc" sx={{ fontSize: "0.8rem" }}>Prix / kg décroissant</MenuItem>
                  <MenuItem value="tonnes_desc" sx={{ fontSize: "0.8rem" }}>Volume disponible (Tonnes)</MenuItem>
                  <MenuItem value="stock_first" sx={{ fontSize: "0.8rem" }}>En stock immédiat d'abord</MenuItem>
                </Select>
              </Box>
            </Box>

            {/* Puces des filtres actifs & Bouton de réinitialisation */}
            {activeFilterCount > 0 && (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap", mt: 1.5, px: 0.5 }}>
                <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 700 }}>
                  Filtres actifs :
                </Typography>
                {searchQuery && (
                  <Chip
                    label={`Recherche : "${searchQuery}"`}
                    size="small"
                    onDelete={() => setSearchQuery("")}
                    sx={{ bgcolor: alpha("#014527", 0.1), color: "#01321d", fontWeight: 700 }}
                  />
                )}
                {selectedCategory !== "Toutes" && (
                  <Chip
                    label={`Catégorie : ${selectedCategory}`}
                    size="small"
                    onDelete={() => setSelectedCategory("Toutes")}
                    sx={{ bgcolor: alpha("#014527", 0.1), color: "#01321d", fontWeight: 700 }}
                  />
                )}
                {selectedStatus !== "all" && (
                  <Chip
                    label={`Disponibilité : ${selectedStatus === "in_stock" ? "En stock" : "Précommande"}`}
                    size="small"
                    onDelete={() => setSelectedStatus("all")}
                    sx={{ bgcolor: alpha("#014527", 0.1), color: "#01321d", fontWeight: 700 }}
                  />
                )}
                {sortBy !== "recommended" && (
                  <Chip
                    label={`Tri personnalisé`}
                    size="small"
                    onDelete={() => setSortBy("recommended")}
                    sx={{ bgcolor: alpha("#d97706", 0.15), color: "#b45309", fontWeight: 700 }}
                  />
                )}
                <Button
                  size="small"
                  onClick={handleResetFilters}
                  startIcon={<RestartAltIcon />}
                  sx={{ textTransform: "none", fontSize: "0.78rem", color: "#d97706", fontWeight: 800, ml: "auto" }}
                >
                  Effacer tous les filtres
                </Button>
              </Box>
            )}
          </Box>

          {/* Grille des Produits */}
          {filteredLots.length === 0 ? (
            <Paper
              elevation={0}
              sx={{
                p: { xs: 4, sm: 6 },
                textAlign: "center",
                bgcolor: "#f9faf9",
                borderRadius: "16px",
                border: "1px dashed rgba(0,0,0,0.15)"
              }}
            >
              <StorefrontIcon sx={{ fontSize: 56, color: "text.secondary", mb: 1.5 }} />
              <Typography variant="h6" sx={{ fontWeight: 800, color: "#01321d", mb: 1 }}>
                Aucun lot agricole ne correspond à vos critères
              </Typography>
              <Typography variant="body2" sx={{ color: "text.secondary", mb: 3, maxWidth: "500px", mx: "auto" }}>
                Essayez d'élargir votre recherche, de changer de province ou de réinitialiser vos filtres.
              </Typography>
              <Button
                variant="contained"
                onClick={handleResetFilters}
                sx={{
                  bgcolor: "#014527",
                  color: "#ffffff",
                  fontWeight: 700,
                  borderRadius: "20px",
                  textTransform: "none",
                  px: 3
                }}
              >
                Réinitialiser tous les filtres
              </Button>
            </Paper>
          ) : (
            <Grid container spacing={3}>
              {filteredLots.map((lot) => {
                const isPreorder = lot.status === "preorder";
                return (
                  <Grid item xs={12} sm={6} md={4} key={lot.id}>
                    <Card
                      elevation={2}
                      sx={{
                        height: "100%",
                        display: "flex",
                        flexDirection: "column",
                        borderRadius: "16px",
                        overflow: "hidden",
                        border: "1px solid rgba(1, 69, 39, 0.12)",
                        transition: "transform 0.25s ease, box-shadow 0.25s ease",
                        "&:hover": {
                          transform: "translateY(-4px)",
                          boxShadow: "0 12px 28px rgba(1, 69, 39, 0.15)"
                        }
                      }}
                    >
                      {/* Image avec badges superposés */}
                      <Box sx={{ position: "relative" }}>
                        <ProductImage
                          src={lot.imageUrl}
                          alt={lot.product}
                          height={185}
                        />

                        {/* Badge Statut en haut à gauche */}
                        <Chip
                          label={isPreorder ? `⏳ Récolte le ${lot.harvestDate || "À venir"}` : "🟢 En Stock Immédiat"}
                          size="small"
                          sx={{
                            position: "absolute",
                            top: 10,
                            left: 10,
                            bgcolor: isPreorder ? "rgba(217, 119, 6, 0.95)" : "rgba(5, 150, 105, 0.95)",
                            color: "#ffffff",
                            fontWeight: 800,
                            fontSize: "0.72rem",
                            backdropFilter: "blur(4px)",
                            boxShadow: "0 2px 6px rgba(0,0,0,0.2)"
                          }}
                        />

                        {/* Badge Grand Libreville en haut à droite */}
                        <Chip
                          icon={<LocalShippingIcon sx={{ fontSize: "14px !important", color: "#ffffff !important" }} />}
                          label="Grand Libreville"
                          size="small"
                          sx={{
                            position: "absolute",
                            top: 10,
                            right: 10,
                            bgcolor: "rgba(1, 50, 29, 0.9)",
                            color: "#ffffff",
                            fontWeight: 700,
                            fontSize: "0.72rem",
                            backdropFilter: "blur(4px)"
                          }}
                        />
                      </Box>

                      {/* Corps de la carte */}
                      <CardContent sx={{ flexGrow: 1, p: 2.5 }}>
                        {/* Catégorie & Délai */}
                        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
                          <Typography variant="caption" sx={{ color: "#059669", fontWeight: 700, textTransform: "uppercase" }}>
                            {lot.category}
                          </Typography>
                          <Typography variant="caption" sx={{ color: "text.secondary", fontSize: "0.72rem" }}>
                            🚚 {lot.deliveryWindow}
                          </Typography>
                        </Box>

                        {/* Nom du produit & Variété */}
                        <Typography
                          variant="h6"
                          component="h3"
                          sx={{
                            fontWeight: 900,
                            color: "#01321d",
                            lineHeight: 1.25,
                            mb: 0.3
                          }}
                        >
                          {lot.product}
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{
                            color: "text.secondary",
                            fontSize: "0.85rem",
                            fontWeight: 600,
                            mb: 1.2
                          }}
                        >
                          {lot.variety}
                        </Typography>

                        {/* Zone de livraison garantie */}
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.6, mb: 1.5 }}>
                          <CheckCircleIcon sx={{ fontSize: 16, color: "#059669" }} />
                          <Typography variant="caption" sx={{ color: "text.primary", fontWeight: 700 }}>
                            Livraison : Grand Libreville (Akanda, Owendo, Libreville)
                          </Typography>
                        </Box>

                        {/* Description brève */}
                        <Typography
                          variant="body2"
                          sx={{
                            color: "text.secondary",
                            fontSize: "0.82rem",
                            lineHeight: 1.45,
                            mb: 2,
                            display: "-webkit-box",
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: "vertical",
                            overflow: "hidden"
                          }}
                        >
                          {lot.description}
                        </Typography>

                        {/* Boîte de prix & volume */}
                        <Paper
                          elevation={0}
                          sx={{
                            p: 1.5,
                            bgcolor: alpha("#014527", 0.05),
                            borderRadius: "10px",
                            border: "1px solid rgba(1, 69, 39, 0.12)"
                          }}
                        >
                          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", mb: 0.5 }}>
                            <Box>
                              <Typography variant="h6" sx={{ fontWeight: 900, color: "#014527", lineHeight: 1 }}>
                                {lot.pricePerKg.toLocaleString()} FCFA
                              </Typography>
                              <Typography variant="caption" sx={{ color: "text.secondary", fontSize: "0.7rem" }}>
                                au kilogramme
                              </Typography>
                            </Box>
                            <Box sx={{ textAlign: "right" }}>
                              <Typography variant="subtitle2" sx={{ fontWeight: 800, color: "#d97706", lineHeight: 1 }}>
                                {lot.pricePerTonne.toLocaleString()} FCFA
                              </Typography>
                              <Typography variant="caption" sx={{ color: "text.secondary", fontSize: "0.7rem" }}>
                                à la tonne
                              </Typography>
                            </Box>
                          </Box>

                          <Divider sx={{ my: 0.8 }} />

                          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                            <Typography variant="caption" sx={{ color: "text.primary", fontWeight: 700 }}>
                              📦 Volume : <strong>{lot.availableTonnes} t</strong>
                            </Typography>
                            <Typography variant="caption" sx={{ color: "text.secondary" }}>
                              Min. {lot.minOrderKg >= 1000 ? `${lot.minOrderKg / 1000} t` : `${lot.minOrderKg} kg`}
                            </Typography>
                          </Box>
                        </Paper>
                      </CardContent>

                      {/* Bouton de commande WhatsApp */}
                      <CardActions sx={{ p: 2, pt: 0 }}>
                        <Button
                          variant="contained"
                          fullWidth
                          startIcon={<WhatsAppIcon sx={{ color: "#ffffff" }} />}
                          onClick={() => handleOpenOrder(lot)}
                          sx={{
                            bgcolor: "#014527",
                            color: "#ffffff",
                            fontWeight: 800,
                            textTransform: "none",
                            borderRadius: "10px",
                            py: 1.1,
                            boxShadow: "0 3px 10px rgba(1, 69, 39, 0.2)",
                            "&:hover": {
                              bgcolor: "#01321d",
                              boxShadow: "0 5px 14px rgba(1, 69, 39, 0.35)"
                            }
                          }}
                        >
                          {isPreorder ? "Précommander ce lot" : "Commander ce lot"}
                        </Button>
                      </CardActions>
                    </Card>
                  </Grid>
                );
              })}
            </Grid>
          )}
        </Container>
      </Box>

      {/* Bannière Grands Comptes & Sourcing B2B */}
      <Box
        sx={{
          bgcolor: "#01321d",
          backgroundImage:
            "radial-gradient(circle at 80% 20%, rgba(245, 158, 11, 0.15) 0%, transparent 45%), radial-gradient(circle at 20% 80%, rgba(16, 185, 129, 0.15) 0%, transparent 45%)",
          color: "#ffffff",
          py: { xs: 5, md: 7 },
          borderTop: "1px solid rgba(255, 255, 255, 0.1)",
          display: { xs: mobileStep === "produits" ? "block" : "none", md: "block" }
        }}
      >
        <Container maxWidth="lg">
          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={8}>
              <Chip
                label="Contrats Cadres & Approvisionnement B2B"
                size="small"
                sx={{
                  bgcolor: "rgba(245, 158, 11, 0.2)",
                  color: "#fbbf24",
                  fontWeight: 800,
                  fontSize: "0.75rem",
                  mb: 1.5
                }}
              />
              <Typography
                variant="h4"
                sx={{
                  fontWeight: 900,
                  fontSize: { xs: "1.6rem", sm: "2.2rem" },
                  lineHeight: 1.25,
                  mb: 1.5
                }}
              >
                Vous êtes un Hôtel, Restaurant, Supermarché ou Exportateur ?
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  color: "rgba(255, 255, 255, 0.85)",
                  fontSize: "0.95rem",
                  lineHeight: 1.6,
                  maxWidth: "700px"
                }}
              >
                Agri Vision Gabon structure des filières régulières avec volumes garantis, 
                tarifs grossistes bord champ et bordereaux de traçabilité complets. 
                Contactez notre cellule commerciale pour établir votre calendrier d'approvisionnement.
              </Typography>
            </Grid>

            <Grid item xs={12} md={4} sx={{ textAlign: { xs: "left", md: "right" } }}>
              <Button
                href="https://wa.me/24104126488?text=Bonjour%20Agri%20Vision%20Gabon%2C%20je%20suis%20un%20acheteur%20professionnel%20et%20je%20souhaite%20un%20devis%20de%20sourcing%20agricole%20group%C3%A9."
                target="_blank"
                rel="noopener noreferrer"
                variant="contained"
                size="large"
                startIcon={<WhatsAppIcon sx={{ color: "#25D366" }} />}
                sx={{
                  bgcolor: "#ffffff",
                  color: "#01321d",
                  fontWeight: 800,
                  fontSize: "0.95rem",
                  px: 3.5,
                  py: 1.4,
                  borderRadius: "30px",
                  textTransform: "none",
                  boxShadow: "0 6px 20px rgba(0,0,0,0.3)",
                  "&:hover": {
                    bgcolor: "#f1f5f2",
                    transform: "translateY(-2px)"
                  }
                }}
              >
                Contacter le Service B2B WhatsApp
              </Button>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Barre d'action rapide flottante sur Mobile */}
      <Box
        sx={{
          display: { xs: mobileStep === "produits" ? "flex" : "none", md: "none" },
          position: "fixed",
          bottom: 20,
          left: "50%",
          transform: "translateX(-50%)",
          zIndex: 1000,
          bgcolor: "#01321d",
          color: "#ffffff",
          borderRadius: "36px",
          p: "5px 12px",
          boxShadow: "0 8px 26px rgba(0,0,0,0.35)",
          alignItems: "center",
          gap: 0.5,
          border: "1px solid rgba(255,255,255,0.25)",
          backdropFilter: "blur(8px)"
        }}
      >
        {mobileStep === "produits" ? (
          <Button
            size="small"
            startIcon={<MapIcon sx={{ fontSize: "17px !important" }} />}
            onClick={() => handleSwitchStep("carte")}
            sx={{
              color: "#ffffff",
              textTransform: "none",
              fontWeight: 800,
              fontSize: "0.8rem",
              px: 1.5,
              borderRadius: "20px"
            }}
          >
            Carte {selectedProvince ? `(${selectedProvince})` : "du Gabon"}
          </Button>
        ) : (
          <Button
            size="small"
            startIcon={<ShoppingBagIcon sx={{ fontSize: "17px !important" }} />}
            onClick={() => handleSwitchStep("produits")}
            sx={{
              color: "#ffffff",
              textTransform: "none",
              fontWeight: 800,
              fontSize: "0.8rem",
              px: 1.5,
              borderRadius: "20px"
            }}
          >
            Récoltes ({filteredLots.length})
          </Button>
        )}

        <Divider orientation="vertical" flexItem sx={{ bgcolor: "rgba(255,255,255,0.3)", my: 0.8 }} />

        <Button
          size="small"
          startIcon={
            <Badge
              badgeContent={activeFilterCount}
              color="warning"
              sx={{ "& .MuiBadge-badge": { fontWeight: 900 } }}
            >
              <TuneIcon sx={{ fontSize: "17px !important" }} />
            </Badge>
          }
          onClick={() => setFilterDrawerOpen(true)}
          sx={{
            color: "#ffffff",
            textTransform: "none",
            fontWeight: 800,
            fontSize: "0.8rem",
            px: 1.5,
            borderRadius: "20px"
          }}
        >
          Filtres
        </Button>
      </Box>

      {/* Tiroir de Filtres Mobile (Bottom Sheet) */}
      <Drawer
        anchor="bottom"
        open={filterDrawerOpen}
        onClose={() => setFilterDrawerOpen(false)}
        PaperProps={{
          sx: {
            borderTopLeftRadius: "22px",
            borderTopRightRadius: "22px",
            maxHeight: "85vh",
            p: { xs: 2.5, sm: 3 },
            bgcolor: "#ffffff"
          }
        }}
      >
        {/* Poignée de tirage */}
        <Box
          sx={{
            width: 44,
            height: 5,
            bgcolor: "grey.300",
            borderRadius: 3,
            mx: "auto",
            mb: 2
          }}
        />

        {/* En-tête */}
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <TuneIcon sx={{ color: "#014527" }} />
            <Typography variant="h6" sx={{ fontWeight: 800, color: "#01321d", fontSize: "1.1rem" }}>
              Filtres de la Centrale d'Achat
            </Typography>
          </Box>
          <IconButton onClick={() => setFilterDrawerOpen(false)} size="small">
            <CloseIcon />
          </IconButton>
        </Box>

        <Divider sx={{ mb: 2.5 }} />

        {/* Section 1 : Périmètre de Livraison */}
        <Typography variant="subtitle2" sx={{ fontWeight: 800, color: "#01321d", mb: 1 }}>
          📍 Périmètre de Livraison Garanti :
        </Typography>
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.8, mb: 2.5 }}>
          <Chip
            icon={<LocalShippingIcon sx={{ fontSize: "16px !important", color: "#ffffff !important" }} />}
            label="Grand Libreville (Akanda, Owendo, Libreville)"
            sx={{
              fontWeight: 800,
              bgcolor: "#014527",
              color: "#ffffff",
              border: "1px solid #014527"
            }}
          />
        </Box>

        {/* Section 3 : Catégorie */}
        <Typography variant="subtitle2" sx={{ fontWeight: 800, color: "#01321d", mb: 1 }}>
          🌾 Catégorie de récolte :
        </Typography>
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.8, mb: 2 }}>
          {CATEGORIES.map((cat) => {
            const isSelected = selectedCategory === cat;
            const count = categoryCounts[cat] ?? 0;
            return (
              <Chip
                key={cat}
                label={`${cat} (${count})`}
                onClick={() => setSelectedCategory(cat)}
                sx={{
                  fontWeight: 700,
                  bgcolor: isSelected ? "#014527" : "#ffffff",
                  color: isSelected ? "#ffffff" : "#01321d",
                  border: isSelected ? "1px solid #014527" : "1px solid rgba(1, 69, 39, 0.2)"
                }}
              />
            );
          })}
        </Box>

        {/* Section 4 : Disponibilité */}
        <Typography variant="subtitle2" sx={{ fontWeight: 800, color: "#01321d", mb: 1 }}>
          📦 Disponibilité bord champ :
        </Typography>
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.8, mb: 2 }}>
          {[
            { id: "all", label: "Tous les statuts" },
            { id: "in_stock", label: "🟢 En stock immédiat" },
            { id: "preorder", label: "⏳ En précommande" }
          ].map((st) => {
            const isSelected = selectedStatus === st.id;
            return (
              <Chip
                key={st.id}
                label={st.label}
                onClick={() => setSelectedStatus(st.id)}
                sx={{
                  fontWeight: 700,
                  bgcolor: isSelected ? "#014527" : "#ffffff",
                  color: isSelected ? "#ffffff" : "#01321d",
                  border: isSelected ? "1px solid #014527" : "1px solid rgba(1, 69, 39, 0.2)"
                }}
              />
            );
          })}
        </Box>

        {/* Section 5 : Tri */}
        <Typography variant="subtitle2" sx={{ fontWeight: 800, color: "#01321d", mb: 1 }}>
          🔄 Trier par :
        </Typography>
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.8, mb: 2.5 }}>
          {[
            { id: "recommended", label: "Recommandé" },
            { id: "price_asc", label: "Prix croissant" },
            { id: "price_desc", label: "Prix décroissant" },
            { id: "tonnes_desc", label: "Volume disponible" },
            { id: "stock_first", label: "En stock d'abord" }
          ].map((sortObj) => {
            const isSelected = sortBy === sortObj.id;
            return (
              <Chip
                key={sortObj.id}
                label={sortObj.label}
                onClick={() => setSortBy(sortObj.id)}
                sx={{
                  fontWeight: 700,
                  bgcolor: isSelected ? "#014527" : "#ffffff",
                  color: isSelected ? "#ffffff" : "#01321d",
                  border: isSelected ? "1px solid #014527" : "1px solid rgba(1, 69, 39, 0.2)"
                }}
              />
            );
          })}
        </Box>

        {/* Actions du tiroir */}
        <Box sx={{ display: "flex", gap: 1.5, mt: "auto", pt: 1 }}>
          <Button
            variant="outlined"
            onClick={handleResetFilters}
            sx={{
              flex: 1,
              borderRadius: "12px",
              borderColor: alpha("#d97706", 0.5),
              color: "#d97706",
              fontWeight: 700,
              textTransform: "none",
              py: 1.2
            }}
          >
            Réinitialiser
          </Button>
          <Button
            variant="contained"
            onClick={() => {
              setFilterDrawerOpen(false);
              handleSwitchStep("produits");
            }}
            sx={{
              flex: 2,
              borderRadius: "12px",
              bgcolor: "#014527",
              color: "#ffffff",
              fontWeight: 800,
              textTransform: "none",
              py: 1.2,
              "&:hover": { bgcolor: "#01321d" }
            }}
          >
            Voir les {filteredLots.length} résultat(s)
          </Button>
        </Box>
      </Drawer>

      {/* Modale d'information professionnelle pour les provinces non disponibles (Phase 2) */}
      <Dialog
        open={unavailableModalOpen}
        onClose={() => setUnavailableModalOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "22px",
            p: { xs: 2.5, sm: 3 },
            bgcolor: "#ffffff"
          }
        }}
      >
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 2 }}>
          <Chip
            label="Phase 2 • Extension Nationale"
            size="small"
            sx={{ bgcolor: "#f1f5f9", color: "#475569", fontWeight: 800, border: "1px solid #cbd5e1", fontSize: "0.74rem" }}
          />
          <IconButton onClick={() => setUnavailableModalOpen(false)} size="small">
            <CloseIcon />
          </IconButton>
        </Box>

        <Typography variant="h5" sx={{ fontWeight: 900, color: "#01321d", mb: 1, fontSize: { xs: "1.3rem", sm: "1.5rem" } }}>
          Province de {unavailableProvinceName}
        </Typography>

        <Typography variant="body2" sx={{ color: "#475569", lineHeight: 1.6, mb: 2.5, fontSize: "0.9rem" }}>
          La Centrale d'Achat Agri Vision Gabon démarre actuellement ses opérations en <strong>phase pilote exclusive</strong> dans la <strong>Province de l'Estuaire</strong> avec livraison garantie dans le <strong>Grand Libreville (Akanda, Owendo, Libreville)</strong>, où {estuaireLotsCount} récoltes certifiées sont d'ores et déjà disponibles à la commande et précommande direct bord champ (livraison garantie sous 24h).
          <br /><br />
          Les coopératives et récoltes de la province de <strong>{unavailableProvinceName}</strong> seront intégrées lors du déploiement national de la <strong>Phase 2</strong>.
        </Typography>

        <Box sx={{ bgcolor: alpha("#014527", 0.04), p: 2, borderRadius: "14px", border: "1px dashed rgba(1,69,39,0.25)", mb: 3 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 800, color: "#014527", mb: 0.5 }}>
            🌾 Vous êtes producteur ou acheteur dans cette province ?
          </Typography>
          <Typography variant="caption" sx={{ color: "text.secondary", display: "block", lineHeight: 1.45 }}>
            Enregistrez dès à présent votre groupement agricole ou formulez votre besoin d'approvisionnement auprès de nos équipes afin de prioriser votre zone lors de l'ouverture de la Phase 2.
          </Typography>
        </Box>

        <Stack spacing={1.5}>
          <Button
            variant="contained"
            onClick={() => {
              setUnavailableModalOpen(false);
              setSelectedProvince("Estuaire");
              handleSwitchStep("produits");
            }}
            sx={{
              bgcolor: "#014527",
              color: "#ffffff",
              fontWeight: 800,
              py: 1.25,
              borderRadius: "12px",
              textTransform: "none",
              fontSize: "0.92rem",
              boxShadow: "0 4px 12px rgba(1,69,39,0.2)",
              "&:hover": { bgcolor: "#01321d" }
            }}
          >
            Voir les {estuaireLotsCount} récoltes dans l'Estuaire →
          </Button>

          <Button
            variant="outlined"
            startIcon={<WhatsAppIcon sx={{ color: "#25D366" }} />}
            href={`https://wa.me/24104126488?text=Bonjour%20Agri%20Vision%20Gabon%2C%20je%20souhaite%20manifester%20mon%20int%C3%A9r%C3%AAt%20pour%20le%20sourcing%20agricole%20dans%20la%20province%20de%20${encodeURIComponent(unavailableProvinceName || "")}.`}
            target="_blank"
            rel="noopener noreferrer"
            sx={{
              borderColor: "rgba(1,69,39,0.35)",
              color: "#014527",
              fontWeight: 700,
              py: 1.1,
              borderRadius: "12px",
              textTransform: "none",
              "&:hover": {
                borderColor: "#014527",
                bgcolor: alpha("#014527", 0.04)
              }
            }}
          >
            Être notifié du lancement dans cette province (WhatsApp)
          </Button>
        </Stack>
      </Dialog>

      {/* Modale de Commande de Lot (WhatsApp + Firestore) */}
      <MarketplaceOrderModal
        open={orderModalOpen}
        lot={activeLot}
        onClose={() => {
          setOrderModalOpen(false);
          setActiveLot(null);
        }}
      />

      {/* Modale des Statistiques de Production (Recharts PieChart & Tableau) */}
      <ProvinceModal
        province={chartModalProvince}
        onClose={() => setChartModalProvince(null)}
        isMobile={isMobile}
      />
    </>
  );
};

export default MarketplacePage;
